import jxLogger from '../../../services/utils/jxLogger';
import { AnchorCalculator, ShapeGeoBuilder } from '@jxUml';

const _name = 'VerticalLineStrategy';

/**
 * Creates a straight vertical line from fromShape to toShape by placing both
 * anchors on the correct border (top/bottom, whichever faces the other shape)
 * and giving them the same world X. Anchors are stored shape-relative (see
 * UmlConnector), so the shared X is computed in world coords first, then
 * converted back to an offset per shape. No-op if either anchor is auto
 * (null) or the connector has waypoints (only handles the plain 2-point case).
 * @param {object} connector
 * @param {object} fromShape
 * @param {object} toShape
 */
export function createStraighVerticalLine(connector, fromShape, toShape) {
    if (!connector.fromAnchorPoint || !connector.toAnchorPoint) return;

    const waypoints = connector.points || [];
    if (waypoints.length > 0) return;

    const fromEffectiveH = ShapeGeoBuilder.effectiveHeight(fromShape);
    const toEffectiveH = ShapeGeoBuilder.effectiveHeight(toShape);

    // World X of the current from-anchor is kept as the shared X for both ends.
    const existingFromWorld = AnchorCalculator.toShape(fromShape, connector.fromAnchorPoint);
    const sharedX = existingFromWorld.x;

    // Anchors are stored shape-relative (see UmlConnector), so sharedX (world-space)
    // is converted back per shape by subtracting that shape's own x.
    //
    // Example: fromShape.x=300, fromAnchorPoint.x=50 -> sharedX (world) = 350.
    // toShape.x=320 -> toAnchorPoint.x = sharedX - toShape.x = 350 - 320 = 30
    //
    // (relative to toShape, clamped to its own width by clamp()).
    // fromY/toY below need NO such conversion: they're set directly to 0 or
    // effectiveH, which are ALREADY shape-relative (0 = shape's own top,
    // effectiveH = shape's own bottom) — there's no world value to convert.
    // toShape below fromShape -> exit fromShape at its bottom, enter toShape at its top (and vice versa).
    const toIsBelow = toShape.y >= fromShape.y;
    const fromY = toIsBelow ? fromEffectiveH : 0;
    const toY = toIsBelow ? 0 : toEffectiveH;

    connector.fromAnchorPoint = {
        x: AnchorCalculator.clamp(sharedX - fromShape.x, 0, fromShape.w),
        y: fromY,
    };
    connector.toAnchorPoint = {
        x: AnchorCalculator.clamp(sharedX - toShape.x, 0, toShape.w),
        y: toY,
    };

    jxLogger(_name).logDebug(
        'run',
        `sharedX=${sharedX} toIsBelow=${toIsBelow} from=${JSON.stringify(connector.fromAnchorPoint)} to=${JSON.stringify(connector.toAnchorPoint)}`,
    );
}
